// @ts-nocheck
// Chart-js types are very very unperformant, so disabling when possible
import { Component, createEffect, createMemo, onMount } from "solid-js";
import { BarController, CategoryScale, Chart, ChartData, ChartDataset, LinearScale, LineController, LineElement, PointElement, Tooltip } from "chart.js";
import { formatSecondsAsDuration } from "./parsing/parseUtils";

Chart.register(LinearScale, Tooltip, PointElement, LineElement, LineController, BarController, CategoryScale);

const corsair = {
  id: "corsair",
  afterInit: (chart) => {
    if(!chart) return;
    chart.corsair = {
      x: 0,
      y: 0,
    };
  },
  afterEvent: (chart, evt) => {
    if(!chart) return;
    const {
      chartArea: { top, bottom, left, right },
    } = chart;
    const { x, y } = evt.event;
    if (x < left || x > right || y < top || y > bottom) {
      chart.corsair = {
        x,
        y,
        draw: false,
      };
      chart.draw();
      return;
    }

    chart.corsair = {
      x,
      y,
      draw: true,
    };

    chart.draw();
  },
  afterDatasetsDraw: (chart, _, opts) => {
    const {
      ctx,
      chartArea: { top, bottom, left, right },
    } = chart;

    if (!chart.corsair || !chart.corsair.draw) {
      return;
    }

    const { x, y, draw } = chart.corsair;

    ctx.lineWidth = opts.width || 0;
    ctx.setLineDash(opts.dash || []);
    ctx.strokeStyle = opts.color || "white";

    ctx.save();
    ctx.beginPath();
    if (opts.vertical) {
      ctx.moveTo(x, bottom);
      ctx.lineTo(x, top);
    }
    if (opts.horizontal) {
      ctx.moveTo(left, y);
      ctx.lineTo(right, y);
    }
    ctx.stroke();
    ctx.restore();
  },
};

export type DataSeries = {
  label: string;
  data: number[];
  color: string;
};

function rgbHexAlpha(hexColor: string, alpha: number): string {
  return `${hexColor}${Math.ceil(255 * alpha)
    .toString(16)
    .toUpperCase()}`;
}
function dataSeriesToChartDatasets(dataSeries: DataSeries[]): ChartDataset[] {
  return dataSeries.map(({ label, data, color, ...rest }) => ({
    label,
    data,
    borderColor: color,
    backgroundColor: color,
    ...rest,
  }));
}

type TimelineChartOptions = {
  xGridLines?: boolean;
  yGridLines?: boolean;
  maxY?: number;
};

export const TimelineChart: Component<{
  data: DataSeries[];
  timestamps: number[];
  title?: string;
  options?: TimelineChartOptions;
  series?: { highlight: boolean; hidden: boolean }[];
}> = (props) => {
  let canvas: HTMLCanvasElement;
  let chart: Chart;

  onMount(() => {
    chart = new Chart(canvas, {
      type: "line",
      data: {
        labels: props.timestamps,
        datasets: dataSeriesToChartDatasets(props.data),
      },
      options: {
        plugins: {
          legend: {
            display: false,
          },
          tooltip: {
            enabled: true,
            intersect: false,
            mode: "index",
            axis: "x",
            padding: 15,
            caretSize: 0,
            displayColors: false,
            footerFont: {
              weight: "normal",
            },
            callbacks: {
              label: (d) => `${d.formattedValue} ${d.dataset.label}`,
              labelTextColor: (d) => d.dataset.borderColor,
              title: (x) => formatSecondsAsDuration(+x[0].label),
              footer: (rs) => (rs.length > 1 ? `${rs.reduce((t, r) => t + r.parsed.y, 0)} total` : null),
            },
          },
          corsair: {
            horizontal: false,
            vertical: true,
            color: "rgba(255,255,255,0.7)",
            dash: [],
            width: 1,
          },
        } as Chart["options"]["plugins"],
        elements: {
          point: {
            radius: 0,
            borderColor: "transparent",
            backgroundColor: "transparent",
            hitRadius: 3,
          },
          line: {
            tension: 0.05,
            borderWidth: 1,
          },
        },
        scales: {
          y: {
            beginAtZero: true,
            bounds: "data",
            min: 0,
            ticks: {
              display: false,
              maxTicksLimit: 5,
            },
            grid: props.options?.yGridLines
              ? {
                  borderColor: "transparent",
                  color: "rgba(255,255,255,0.1)",
                }
              : {},
          },
          x: {
            grace: 20,
            ticks: {
              autoSkipPadding: 100,
              maxTicksLimit: 5,
              callback: (_, index) => (props.timestamps[index] % (60 * 5) === 0 ? formatSecondsAsDuration(props.timestamps[index]) : null),
            },
            grid: props.options?.xGridLines
              ? {
                  borderColor: "transparent",
                  color: "rgba(255,255,255,0.1)",
                }
              : {},
          },
        },
      },
      plugins: [corsair],
    });
  });

  const max = createMemo(() => props.options.maxY ?? Math.max(...props.data.map((d) => Math.max(...d.data))));
  createEffect(() => {
    if (chart && max()) {
      chart.options.scales.y.max = max() * 1.1;
      chart.update();
    }
  });

  createEffect(() => {
    if (chart && props.data) {
      chart.data.datasets = dataSeriesToChartDatasets(props.data);
      chart.update();
    }
  });

  createEffect(() => {
    if (chart && props.series) {
      const hasAnyHighlight = props.series.some((s) => s.highlight);
      chart.data.datasets.forEach((d, i) => {
        d.hidden = props.series[i].hidden;
        d.borderColor = props.series[i].highlight || !hasAnyHighlight ? props.data[i].color : rgbHexAlpha(props.data[i].color, 0.3);
        d.animation = { duration: 150 };
      });
      chart.update();
    }
  });
  return (
    <div class="bg-black/20 rounded p-2 my-4">
      <h3 class="text-gray-200  text-xs font-bold uppercase mb-2">{props.title}</h3>
      <canvas ref={canvas} width="400" height="200" />
    </div>
  );
};
